/**
 * Profile Event Essentials Page
 * Manage event types, payment modes, and performance essentials
 * Part of artist profile flow with save/cancel and loading states
 *
 * @author Varun Pandey
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../utils/cn';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import { EventPricingTab, BillingDetailsTab, ContactDetailsTab, TechRiderTblTab } from './event-essentials';
import { ProfileCompletionBlocksControl } from '@/components/profile/ProfileCompletionBlocksControl';

const VALID_TABS = ['pricing', 'billing', 'secondary', 'tech'] as const;
type EventEssentialsTab = (typeof VALID_TABS)[number];

function getInitialEventEssentialsTab(searchTab: string | null): EventEssentialsTab {
    if (searchTab === 'contact-details' || searchTab === 'secondary') return 'secondary';
    if (searchTab && VALID_TABS.includes(searchTab as EventEssentialsTab)) return searchTab as EventEssentialsTab;
    if (typeof sessionStorage === 'undefined') return 'pricing';
    const stored = sessionStorage.getItem(STORAGE_KEYS.EVENT_ESSENTIALS_ACTIVE_TAB);
    if (stored && VALID_TABS.includes(stored as EventEssentialsTab)) return stored as EventEssentialsTab;
    return 'pricing';
}

const tabs = [
    { id: 'pricing' as const, label: 'Event Pricing' },
    { id: 'billing' as const, label: 'Billing Details' },
    { id: 'secondary' as const, label: 'Contact Details' },
    { id: 'tech' as const, label: 'Tech Rider & TBL' },
];

export const ProfileEventEssentialsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState<EventEssentialsTab>(() => getInitialEventEssentialsTab(tabFromUrl));

    useEffect(() => {
        const urlTab = getInitialEventEssentialsTab(searchParams.get('tab'));
        if (searchParams.get('tab') !== null) setActiveTab(urlTab);
    }, [searchParams]);

    useEffect(() => {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEYS.EVENT_ESSENTIALS_ACTIVE_TAB, activeTab);
        }
    }, [activeTab]);

    return (
        <div className="w-full bg-[#0B0B0D] text-white">
            <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-24">
                <header className="mb-8">
                    <div className="mb-6 flex items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/profile')}
                            className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span className="text-sm font-medium">Back to Profile</span>
                        </button>
                        <ProfileCompletionBlocksControl />
                    </div>
                    <h1 className="text-3xl lg:text-4xl font-black text-white tracking-tighter mb-8">
                        Event Essentials
                    </h1>

                    <div className="border-b border-[#3A3A3C]">
                        <nav className="flex gap-6 lg:gap-8" aria-label="Event essentials sections">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={cn(
                                        'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                                        activeTab === tab.id
                                            ? 'text-white'
                                            : 'text-[#8E8E93] hover:text-white'
                                    )}
                                    aria-current={activeTab === tab.id ? 'page' : undefined}
                                >
                                    {tab.label}
                                    {activeTab === tab.id && (
                                        <motion.span
                                            layoutId="event-essentials-tab-underline"
                                            className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                                            transition={{ type: 'spring', duration: 0.3 }}
                                        />
                                    )}
                                </button>
                            ))}
                        </nav>
                    </div>
                </header>

                <AnimatePresence mode="wait">
                    {activeTab === 'pricing' && <EventPricingTab />}
                    {activeTab === 'billing' && <BillingDetailsTab />}
                    {activeTab === 'secondary' && <ContactDetailsTab />}
                    {activeTab === 'tech' && <TechRiderTblTab />}
                </AnimatePresence>
            </div>
        </div>
    );
};
